import React, {FC} from "react";
import {Grid, Checkbox, FormControlLabel} from "@material-ui/core";

import TypeSelector from "./type_selector";
import TypeDetail from "./type_detail";
import isPossibleEnumType from "./util/is_possible_enum_type";

import {
  SchemaOptionalType,
  SchemaPropertyWithOptionalType,
  SchemaType,
} from "common/flowmaster/types/schema";
import {
  ActionDefinition,
  WorkflowTask,
} from "common/flowmaster/types/task_config";
import {
  Prompt,
  WorkflowContext,
  WorkflowInputs,
} from "common/flowmaster/types/workflow";
import {OrganisationId} from "common/types/organisation";
import getCoreTypeFromOptionalType from "./util/get_core_type_from_optional_type";
import isOptionalType from "./util/is_optional_type";

interface ItemsEditorProps {
  items: SchemaPropertyWithOptionalType;
  onUpdateItems: (items: SchemaPropertyWithOptionalType) => void;
  priorTasks?: WorkflowTask[];
  context?: WorkflowContext;
  inputs?: WorkflowInputs;
  prompts?: Prompt[];
  actionDefinitions?: ActionDefinition[];
  organisationId?: OrganisationId;
  useDataStoreProperties?: boolean;
}

const ItemsEditor: FC<ItemsEditorProps> = ({
  items,
  onUpdateItems,
  priorTasks,
  context,
  inputs,
  prompts,
  actionDefinitions,
  organisationId,
  useDataStoreProperties = true,
}) => {
  const isEnum = Boolean(items.enum);

  const handleTypeChange = (type: SchemaPropertyWithOptionalType["type"]) => {
    const updatedItems: SchemaPropertyWithOptionalType = {...items, type};

    if (type === "array") {
      updatedItems.items = updatedItems.items || {type: "string"};
    } else {
      delete updatedItems.items;
    }

    if (type === "object") {
      updatedItems.properties = items.properties || {};
    } else {
      delete updatedItems.properties;
    }

    if (!isPossibleEnumType(type)) {
      delete updatedItems.enum;
    } else if (isEnum) {
      updatedItems.enum = [];
    }

    onUpdateItems(updatedItems);
  };

  const toggleEnum = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedItems = {...items};
    if (event.target.checked && !updatedItems.enum) {
      updatedItems.enum = [];
    } else {
      delete updatedItems.enum;
    }
    onUpdateItems(updatedItems);
  };

  const onEnumUpdate = (enumValues: SchemaPropertyWithOptionalType["enum"]) => {
    onUpdateItems({...items, enum: enumValues});
  };

  const coreType = isOptionalType(items.type)
    ? getCoreTypeFromOptionalType(items.type as SchemaOptionalType)
    : (items.type as SchemaType);

  return (
    <Grid container>
      <Grid item xs={12}>
        <div style={{display: "flex", alignItems: "center", width: "20em"}}>
          <TypeSelector
            value={coreType}
            onChange={type =>
              handleTypeChange(
                isOptionalType(items.type) ? [type, "null"] : type,
              )
            }
          />
          {isPossibleEnumType(items.type) && (
            <FormControlLabel
              control={<Checkbox checked={isEnum} onChange={toggleEnum} />}
              label="Enum"
            />
          )}
        </div>
      </Grid>
      <TypeDetail
        property={items}
        isEnum={isEnum}
        onUpdate={(name, updatedItems) => onUpdateItems(updatedItems)}
        onEnumUpdate={onEnumUpdate}
        priorTasks={priorTasks}
        context={context}
        inputs={inputs}
        prompts={prompts}
        actionDefinitions={actionDefinitions}
        organisationId={organisationId}
        useDataStoreProperties={useDataStoreProperties}
      />
    </Grid>
  );
};

export default ItemsEditor;
